import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Calendar, MessageCircle, BookOpen, HelpCircle, Menu } from 'lucide-react';

import { ROUTES } from '../../constants/routes';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path: string;
  matchPaths?: string[];
}

const navigationItems: NavItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    path: ROUTES.STUDENT_LANDING,
  },
  {
    id: 'schedule',
    label: 'Schedule',
    icon: Calendar,
    path: ROUTES.STUDENT_SCHEDULE,
  },
  {
    id: 'messages',
    label: 'Messages',
    icon: MessageCircle,
    path: ROUTES.STUDENT_MESSAGES,
  },
  {
    id: 'journal',
    label: 'Journal',
    icon: BookOpen,
    path: ROUTES.STUDENT_JOURNAL,
  },
];

const mobileLabels: Record<string, string> = {
  home: 'Home',
  schedule: 'Schedule',
  messages: 'Messages',
  journal: 'Journal',
};

function isItemActive(item: NavItem, pathname: string): boolean {
  if (pathname === item.path) return true;
  const prefixes = item.matchPaths ?? [item.path];
  return prefixes.some((prefix) => pathname.startsWith(prefix + '/') || pathname === prefix);
}

interface StudentSidebarProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

export function StudentSidebar({ isOpen = false, onToggle }: StudentSidebarProps) {
  const location = useLocation();
  const isExpanded = isOpen;

  return (
    <>
      {/* Backdrop - closes sidebar when clicking outside (desktop only) */}
      {isExpanded && onToggle && (
        <div
          className="hidden md:block fixed inset-0 z-[59] bg-black/20"
          onClick={onToggle}
          aria-hidden
        />
      )}

      {/* ─── Desktop Sidebar ─── */}
      <div
        className={`hidden md:block fixed top-0 left-0 bottom-0 bg-[#1a1818] border-r border-white/10 transition-all duration-300 z-[60] overflow-hidden ${
          isExpanded ? 'w-64' : 'w-20'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          {onToggle && (
            <div className="flex items-center h-16 shrink-0 border-b border-white/10 px-3">
              {isExpanded ? (
                <div className="flex items-center justify-between gap-2 w-full">
                  <Link
                    to={ROUTES.STUDENT_LANDING}
                    className="flex items-center gap-2 hover:opacity-80 min-w-0 pl-2"
                  >
                    <img
                      src="/rollops-logo.png"
                      alt="RollOps"
                      className="w-8 h-8 object-contain shrink-0"
                    />
                    <span className="text-xl font-bold text-[#EBFFEE] whitespace-nowrap truncate">
                      Roll<span className="text-[#9B1421]">O</span>ps
                    </span>
                  </Link>
                  <button
                    onClick={onToggle}
                    className="w-8 h-8 flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors shrink-0"
                    aria-label="Toggle sidebar"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full">
                  <button
                    onClick={onToggle}
                    className="w-8 h-8 flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors"
                    aria-label="Toggle sidebar"
                  >
                    <Menu className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Navigation Items */}
          <nav className="flex-1 pt-3 pb-6 px-3 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = isItemActive(item, location.pathname);

              return (
                <Link
                  key={item.id}
                  to={item.path}
                  className={`
                    w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all relative group overflow-hidden
                    ${isActive
                      ? 'bg-rollops-primary/20 text-text-primary'
                      : 'text-text-secondary hover:bg-white/5 hover:text-text-primary'
                    }
                  `}
                  title={!isExpanded ? item.label : undefined}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-rollops-primary rounded-r" />
                  )}
                  <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-[#EBFFEE]' : ''}`} />
                  <span
                    className={`font-medium text-sm whitespace-nowrap transition-opacity duration-300 ${
                      isExpanded ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Contact Support */}
          <div className="px-3 pb-6">
            <button
              onClick={() => window.open('mailto:support@rollops.pro', '_blank')}
              className="w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-all overflow-hidden text-text-secondary hover:bg-white/5 hover:text-text-primary"
              title={!isExpanded ? 'Contact Support' : undefined}
            >
              <HelpCircle className="w-5 h-5 shrink-0" />
              <span
                className={`font-medium text-sm whitespace-nowrap transition-opacity duration-300 ${
                  isExpanded ? 'opacity-100' : 'opacity-0'
                }`}
              >
                Contact Support
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ─── Mobile Bottom Navigation ─── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#1a1818] border-t border-white/10 z-40">
        <div className="flex items-center justify-around px-1 pt-2 pb-[env(safe-area-inset-bottom,8px)]">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = isItemActive(item, location.pathname);

            return (
              <Link
                key={item.id}
                to={item.path}
                className={`
                  flex flex-col items-center gap-1 px-3 py-1.5 rounded-lg transition-colors relative min-w-0
                  ${isActive
                    ? 'text-[#EBFFEE]'
                    : 'text-text-secondary active:text-text-primary'
                  }
                `}
              >
                {isActive && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-rollops-primary rounded-b" />
                )}
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-tight truncate max-w-full">
                  {mobileLabels[item.id] ?? item.label}
                </span>
              </Link>
            );
          })}
        </div>
      </nav>
    </>
  );
}
